import './ErrorCatalogue.css';

import { Link } from 'react-router-dom';

import { ErrorCatalogueDocument, ErrorCatalogueEntry, ErrorCodeLevel } from '../api/errors';
import { useErrorCatalogue } from '../query/errors';
import Markdown from './Markdown';

const LEVEL_LABEL: Record<ErrorCodeLevel, string> = {
  envelope: 'Envelope',
  item: 'Item',
};

const LEVEL_HINT: Record<ErrorCodeLevel, string> = {
  envelope: "Appears as the response's top-level code",
  item: 'Appears as a code inside the errors array',
};

const asDate = (iso: string) => iso.slice(0, 10);

function Badges({ entry }: { entry: ErrorCatalogueEntry }) {
  return (
    <div className="errors-badges">
      {entry.retiredAt && (
        <span
          className="errors-badge errors-badge--retired"
          title={`No service has published this code since ${asDate(
            entry.retiredAt,
          )}. The page is kept so the URI keeps resolving.`}
        >
          retired
        </span>
      )}
      {entry.scope === 'shared' && (
        <span
          className="errors-badge errors-badge--shared"
          title="Meaning is fixed platform-wide — more than one service can return it"
        >
          shared
        </span>
      )}
      {entry.levels.map((level) => (
        <span className="errors-badge errors-badge--level" key={level} title={LEVEL_HINT[level]}>
          {LEVEL_LABEL[level]}
        </span>
      ))}
      {entry.statuses.map((status) => (
        <span className="errors-badge errors-badge--status" key={status}>
          {status}
        </span>
      ))}
    </div>
  );
}

function Crumbs({ doc, code }: { doc: ErrorCatalogueDocument | null; code?: string }) {
  return (
    <nav className="errors-crumbs">
      <Link to="/">SkyMap Docs</Link>
      <span>/</span>
      <Link to="/errors">Errors</Link>
      {doc && (
        <>
          <span>/</span>
          {code ? (
            <Link to={`/errors/${doc.catalogueId}`}>{doc.displayName}</Link>
          ) : (
            <span>{doc.displayName}</span>
          )}
        </>
      )}
      {code && (
        <>
          <span>/</span>
          <span>{code}</span>
        </>
      )}
    </nav>
  );
}

function Conflicts({ doc }: { doc: ErrorCatalogueDocument }) {
  if (!doc.conflicts.length) {
    return null;
  }
  return (
    <div className="errors-notice errors-notice--danger">
      Shared codes were published with conflicting wording by more than one service. The most recent
      publisher&rsquo;s text is shown; the disagreement needs resolving at the source.
      <ul>
        {doc.conflicts.map((c) => (
          <li key={`${c.path}-${c.field}-${c.services.join('-')}`}>
            <code>{c.path}</code> — {c.field} differs between {c.services.join(' and ')}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Explains a retired page to whoever just followed its URI out of a response. */
function RetiredNotice({ entry }: { entry: ErrorCatalogueEntry }) {
  return (
    <div className="errors-notice errors-notice--warning">
      No SkyMap service has published this code since <strong>{asDate(entry.retiredAt!)}</strong>,
      so you should not expect to receive it from a current API. The page is kept because the
      identifier is permanent — a response captured earlier may still carry it.
    </div>
  );
}

// ── Index ────────────────────────────────────────────────────────────────────

function CodeCard({
  entry,
  withPermalink,
}: {
  entry: ErrorCatalogueEntry;
  withPermalink: boolean;
}) {
  return (
    <div className={`errors-card${entry.retiredAt ? ' errors-card--retired' : ''}`}>
      <div className="errors-card-head">
        <Link className="errors-code" to={entry.path}>
          {entry.code}
        </Link>
        <Badges entry={entry} />
      </div>
      <p className="errors-card-title">{entry.title}</p>
      <Markdown source={entry.description} />
      {withPermalink && (
        <Link className="errors-permalink" to={entry.path}>
          {entry.path}
        </Link>
      )}
    </div>
  );
}

function CatalogueIndex({ doc }: { doc: ErrorCatalogueDocument }) {
  const current = doc.codes.filter((c) => !c.retiredAt);
  const retired = doc.codes.filter((c) => c.retiredAt);
  const sample = current[0] ?? doc.codes[0];

  return (
    <>
      <h1 className="errors-title">{doc.displayName} — error catalogue</h1>
      <p className="errors-meta">
        {current.length} code{current.length === 1 ? '' : 's'}
        {retired.length > 0 && ` · ${retired.length} retired`}
      </p>

      <div className="errors-intro">
        <p>
          Every error this service returns carries a <code className="md-code">type</code> URI (RFC
          9457, Problem Details for HTTP APIs). Open it and you land on the page for that exact
          failure.
        </p>
        <pre className="errors-sample">
          {`{
  "type": "${sample?.path ?? '/errors/…'}",
  "title": "${sample?.title ?? '…'}",
  "status": ${sample?.statuses[0] ?? 400}
}`}
        </pre>
        <p>
          These URIs are permanent. A code&rsquo;s page never moves and never changes meaning — when
          behaviour changes, a new code is issued instead, and a code that is no longer returned is
          marked retired rather than removed.
        </p>
      </div>

      <Conflicts doc={doc} />

      {doc.codes.length === 0 ? (
        <p className="errors-empty">This catalogue lists no codes.</p>
      ) : (
        <>
          <div className="errors-list">
            {current.map((entry) => (
              <CodeCard entry={entry} key={entry.path} withPermalink={true} />
            ))}
          </div>

          {retired.length > 0 && (
            <>
              <h2 className="errors-section-title">Retired codes</h2>
              <p className="errors-meta">
                No longer published by any service. Kept because a response captured earlier may
                still carry the URI.
              </p>
              <div className="errors-list">
                {retired.map((entry) => (
                  <CodeCard entry={entry} key={entry.path} withPermalink={true} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

// ── One code ─────────────────────────────────────────────────────────────────

function CodeDetail({ doc, entry }: { doc: ErrorCatalogueDocument; entry: ErrorCatalogueEntry }) {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const current = doc.codes.filter((c) => !c.retiredAt).length;

  return (
    <>
      <div className="errors-detail-head">
        <h1 className="errors-title">{entry.title}</h1>
        <div className="errors-card-head">
          <span className="errors-code">{entry.code}</span>
          <Badges entry={entry} />
        </div>
      </div>

      {entry.retiredAt && <RetiredNotice entry={entry} />}

      <Markdown source={entry.description} />

      <p className="errors-uri">
        This identifier is permanent — safe to match on, and it will never be reused for a different
        failure.
        <code>{`${origin}${entry.path}`}</code>
      </p>

      <p className="errors-source">
        <Link to={`/errors/${doc.catalogueId}`}>
          All {doc.displayName} codes ({current})
        </Link>
      </p>
    </>
  );
}

// ── Unknown code ─────────────────────────────────────────────────────────────

function UnknownCode({ doc, code }: { doc: ErrorCatalogueDocument; code: string }) {
  const current = doc.codes.filter((c) => !c.retiredAt);
  return (
    <>
      <h1 className="errors-title">No such error code</h1>
      <div className="errors-notice errors-notice--warning">
        <code>
          /errors/{doc.catalogueId}/{code}
        </code>{' '}
        has never been published to the {doc.displayName} catalogue, so the link is most likely
        mistyped. Codes that have been withdrawn are kept and marked retired rather than removed, so
        this is not one of those.
      </div>
      <p className="errors-meta">
        The {current.length} code{current.length === 1 ? '' : 's'} this service can currently
        return:
      </p>
      <div className="errors-list">
        {current.map((entry) => (
          <div className="errors-card" key={entry.path}>
            <div className="errors-card-head">
              <Link className="errors-code" to={entry.path}>
                {entry.code}
              </Link>
              <Badges entry={entry} />
            </div>
            <p className="errors-card-title">{entry.title}</p>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Container ────────────────────────────────────────────────────────────────

interface Props {
  /** The catalogue: a service's id, or 'common' for codes no service owns. */
  catalogueId: string;
  /** A single code's page; omit for the catalogue index. */
  code?: string;
}

export default function ErrorCatalogueContent({ catalogueId, code }: Props) {
  const query = useErrorCatalogue(catalogueId);
  const doc = query.data ?? null;

  // `path` is the producer's, and is what the URL was built from — match on it
  // rather than rebuilding a path from `code`.
  const entry =
    doc && code ? doc.codes.find((c) => c.path === `/errors/${catalogueId}/${code}`) : null;

  return (
    <div className="errors">
      <Crumbs code={code} doc={doc} />

      {query.isPending && <p className="errors-empty">Loading error catalogue…</p>}

      {query.isError && (
        <>
          <h1 className="errors-title">No error catalogue here</h1>
          <div className="errors-notice errors-notice--warning">
            No error catalogue is published for <code>{catalogueId}</code>.
          </div>
          <p className="errors-source">
            <Link to="/errors">All error catalogues</Link>
          </p>
        </>
      )}

      {doc && !code && <CatalogueIndex doc={doc} />}
      {doc && code && entry && <CodeDetail doc={doc} entry={entry} />}
      {doc && code && !entry && <UnknownCode code={code} doc={doc} />}
    </div>
  );
}
